import React, { useEffect, useCallback, useRef, useMemo, useState } from 'react';
import { useTranslations } from '@core/js/hooks/i18n';
import { useDeviceSelector } from '@core/js/store/hooks';
import { compose, prop } from 'ramda';

import withField from '@core/js/components/common/Fields/withField';
import { Control } from '@core/js/components/common/Fields/Control';
import DropDownIcon from '@core/js/components/common/Fields/Icons/DropDownIcon';
import ValidationIcon from '@core/js/components/common/Fields/Icons/ValidationIcon';

import style from '@core/js/components/common/Fields/Select/Select.scss';

export const EditableSelectBare = ({
    /* rff render props */
    input,
    meta = {},

    /* user provided props */
    id,
    name,
    label,
    hint,
    placeholder = '',
    className,
    classNames,
    options,
    disabled,
    readOnly,
    setValue,
    parse,
    onMouseDown,
    infoTooltip,
    isLabelAsteriskEnabled,
    isPlaceholderAsteriskEnabled,

    ...selectDomProps
}) => {
    const selectRef = useRef();
    const translate = useTranslations();
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const preventClick = useCallback(event => event.preventDefault(), []);
    const [isOpen, setIsOpen] = useState(false);
    const [activeItem, setActiveItem] = useState(
        options.find(option => option.value === input.value)?.label || ''
    );

    const toggleDropdown = useCallback(() => {
        if (!readOnly) {
            setIsOpen(prev => !prev);
        }
    }, [readOnly]);

    const closeDropdown = useCallback((e) => {
        if (!selectRef.current?.contains(e.target)) {
            setIsOpen(false);
        }
    }, []);

    const handleOptionClick = (item) => {
        input.onChange(item.value);
        setActiveItem(item.label);
        setIsOpen(false);
        input.onBlur();
    };

    const getLabelForValue = useCallback(
        (value) => options.find(option => option.value === value)?.label || '',
        [options]
    );

    const selectClassName = useMemo(
        () =>
            [
                style.select,
                classNames?.select,
                placeholder && (input.value == null || input.value === '') ? style.empty : ''
            ]
                .filter(Boolean)
                .join(' '),
        [classNames?.select, placeholder, input.value]
    );

    useEffect(() => {
        if (placeholder && !input.value || !options.length || !setValue) {
            return;
        }

        const isValueMissing = options.every(item => item.value.toString() !== input.value.toString());

        if (isValueMissing) {
            const newValue = parse ? parse(options[0].value) : options[0].value;

            setValue(newValue);
        }
    }, [input.value, options, parse, setValue, placeholder]);

    useEffect(() => {
        if (isDesktop && !disabled && meta.active) {
            selectRef?.current?.focus();
        }
    }, [isDesktop, disabled, meta.active]);

    useEffect(() => {
        document.addEventListener('click', closeDropdown);
        return () => document.removeEventListener('click', closeDropdown);
    }, [closeDropdown]);

    return (
        <Control
            id={id}
            meta={meta}
            className={`${className} ${readOnly ? classNames?.readOnly : ''} ${isOpen ? classNames?.openedListControl : ''} ${classNames?.listControl}`} /* Lugano changes */
            label={label}
            infoTooltip={infoTooltip}
            isLabelAsteriskEnabled={isLabelAsteriskEnabled}
            hint={hint}
            readOnly={readOnly}
            animate={true}
            classNames={{
                content: `${style.content} ${disabled || readOnly ? style.disabled : ''}`,
                ...classNames
            }}
        >
            <input
                id={id}
                ref={selectRef}
                tabIndex={disabled || readOnly ? -1 : 0}
                className={`${selectClassName} ${isOpen ? classNames?.openedList : ''}`}
                name={name}
                {...input}
                {...selectDomProps}
                type="button"
                aria-label={selectDomProps['aria-label'] || translate('playtech.mobile.system.select.aria-label')}
                {...meta.submitting ? { disabled: true } : {}}
                readOnly={readOnly}
                onMouseDown={readOnly ? preventClick : onMouseDown}
                onClick={toggleDropdown}
                value={getLabelForValue(input?.value)}
                onChange={e => e.target.value}
            />
                {isOpen && (
                    <ul className={classNames?.optionsList}>
                        {(placeholder || isPlaceholderAsteriskEnabled) && (
                            <li className={`${classNames?.optionItem} hidden`}>
                                {`${placeholder}${isPlaceholderAsteriskEnabled ? '*' : ''}`}
                            </li>
                        )}
                        {options
                            .filter(item => options.length <= 2 || item.value !== input.value)
                            .map(item => (
                                <li
                                    key={item.value}
                                    value={item.value}
                                    data-active={item.value === activeItem ? true : undefined}
                                    className={`${classNames?.optionItem || ''} ${item.value === input.value ? classNames?.activeItem : ''}`}
                                    onClick={() => handleOptionClick(item)}
                                >
                                    {item.label}
                                </li>
                            ))
                        }
                    </ul>
                )}

                {meta.touched && !meta.active && !readOnly && (
                    <ValidationIcon
                        animate={true}
                        isValid={!meta.error}
                        aria-label={meta.error}
                        tabIndex={meta.error ? 0 : -1}
                        className={`${classNames?.validationIcon || ''} ${classNames?.validationIconCls}`}
                    />
                )}

                <DropDownIcon className={`${classNames?.dropDownIcon || ''} ${classNames?.dropDownIconCls}`} disabled={disabled || readOnly} />
        </Control>
    );
};

EditableSelectBare.defaultProps = {
    options: [],
    className: '',
    classNames: {},
    placeholder: null
};

export const withFieldProps = Wrapped => props =>
    <Wrapped
        enableSelfValueChange={true}
        {...props}
    />;

const enhance = compose(
    withFieldProps,
    withField
);

export const EditableSelect = enhance(EditableSelectBare);
